import { useEffect, useState, useCallback } from 'react';
import {
  FolderOpen,
  Plus,
  Search,
  Filter,
  X,
} from 'lucide-react';
import { useStudioStore } from '../stores/studioStore';
import ProjectCard from '../components/projects/ProjectCard';
import type { Project, ProjectStatus } from '../types';

const STATUS_FILTERS: { label: string; value: ProjectStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Review', value: 'review' },
  { label: 'Published', value: 'published' },
  { label: 'Archived', value: 'archived' },
];

export default function ProjectsPage() {
  const {
    projects,
    projectsLoading,
    loadProjects,
    setActiveProject,
    loadFullProject,
    createProject,
    renameProject,
    duplicateProject,
    deleteProject,
  } = useStudioStore();
  const setCanvasDimensions = useStudioStore((s) => s.setCanvasDimensions);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleOpenProject = useCallback(
    async (project: Project) => {
      const full = await loadFullProject(project.id);
      if (!full) return;
      const state = full.canvas_state;
      if (state?.dimensions) {
        setCanvasDimensions(state.dimensions.width, state.dimensions.height);
      }
      setActiveProject(full);
    },
    [setCanvasDimensions, setActiveProject, loadFullProject]
  );

  const handleRename = useCallback(
    async (id: string, newName: string) => {
      await renameProject(id, newName);
    },
    [renameProject]
  );

  const handleDuplicate = useCallback(
    async (id: string) => {
      await duplicateProject(id);
    },
    [duplicateProject]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteProject(id);
    },
    [deleteProject]
  );

  const handleNewProject = useCallback(async () => {
    const project = await createProject(
      'Untitled Project',
      '',
      { width: 1200, height: 400 }
    );
    if (project) {
      setCanvasDimensions(1200, 400);
      setActiveProject(project);
    }
  }, [createProject, setCanvasDimensions, setActiveProject]);

  const filtered = projects.filter((p) => {
    const matchesSearch =
      search === '' ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.description?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold text-zinc-100 flex items-center gap-2">
              <FolderOpen size={20} className="text-emerald-400" />
              Projects
            </h1>
            <p className="text-sm text-zinc-500">
              {filtered.length} project{filtered.length !== 1 ? 's' : ''}
              {statusFilter !== 'all' && ` (${statusFilter.replace('_', ' ')})`}
            </p>
          </div>
          <button
            onClick={handleNewProject}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium transition-colors"
          >
            <Plus size={16} />
            New Project
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-8 py-2 rounded-lg bg-zinc-900/50 border border-white/[0.06] text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-500/30 focus:ring-1 focus:ring-cyan-500/20 transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400 transition-colors"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Filter size={14} className="text-zinc-500 mr-1" />
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  statusFilter === f.value
                    ? 'bg-cyan-500/15 text-cyan-300'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {projectsLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="rounded-xl bg-zinc-900/50 border border-white/[0.04] animate-pulse"
              >
                <div className="aspect-video bg-zinc-800/30" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-zinc-800/50 rounded w-3/4" />
                  <div className="h-2 bg-zinc-800/30 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onOpen={handleOpenProject}
                onRename={handleRename}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className="p-12 rounded-xl bg-zinc-900/30 border border-white/[0.04] text-center">
            <FolderOpen size={32} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-400">
              {search || statusFilter !== 'all'
                ? 'No projects match your filters'
                : 'No projects yet'}
            </p>
            <p className="text-xs text-zinc-600 mt-1">
              {search || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Create your first project to get started'}
            </p>
            {!search && statusFilter === 'all' && (
              <button
                onClick={handleNewProject}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium transition-colors"
              >
                <Plus size={16} />
                New Project
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
